import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Clock, AlertTriangle, CheckCircle, Search, Pencil, FileDown, Loader2, Building2, Wrench } from 'lucide-react';
import { SortSelect } from '../../components/SortSelect';
import { useServerList } from '../../hooks/useServerList';
import Pagination from '../../components/Pagination';
import { exportTableToPDF } from '../../utils/html2pdfExport';
import { ONCELIK_PDF, ARIZA_DURUM_PDF, slaStyle, kirmiziMetin } from '../../utils/pkPdfStyles';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ONCELIK_CFG = {
  kritik: { bg: 'bg-red-600', label: 'Kritik' },
  yuksek: { bg: 'bg-orange-600', label: 'Yüksek' },
  normal: { bg: 'bg-blue-600', label: 'Normal' },
  dusuk: { bg: 'bg-gray-600', label: 'Düşük' }
};

const DURUM_CFG = {
  acik: { bg: 'bg-red-900/40 border-red-700', icon: AlertTriangle, label: 'Açık', text: 'text-red-400' },
  atandi: { bg: 'bg-yellow-900/30 border-yellow-700', icon: Clock, label: 'Atandı', text: 'text-yellow-400' },
  mudahale_edildi: { bg: 'bg-blue-900/30 border-blue-700', icon: Clock, label: 'Müdahale', text: 'text-blue-400' },
  cozuldu: { bg: 'bg-green-900/30 border-green-700', icon: CheckCircle, label: 'Çözüldü', text: 'text-green-400' },
  kapandi: { bg: 'bg-gray-800/50 border-gray-700', icon: CheckCircle, label: 'Kapandı', text: 'text-gray-400' }
};

const EMPTY_FORM = { ekipman_id: '', bina_id: '', aciklama: '', oncelik: 'normal', atanan_firma_id: '' };

export default function PKArizalar() {
  const { user } = useAuth();
  const readOnly = !!user?.is_viewer;
  // Özet kartları için tam liste (client-side aggregation)
  const [arizalarTumu, setArizalarTumu] = useState([]);
  const [ekipmanlar, setEkipmanlar] = useState([]);
  const [binalar, setBinalar] = useState([]);
  const [firmalar, setFirmalar] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [selectedAriza, setSelectedAriza] = useState(null);
  const [formData, setFormData] = useState({...EMPTY_FORM});
  const [closeData, setCloseData] = useState({ cozum_aciklamasi: '', durum: 'cozuldu' });
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Sort mapping
  const SORT_MAP = {
    newest: 'created_at:desc',
    oldest: 'created_at:asc',
    oncelik_yuksek: 'oncelik:asc',
    oncelik_dusuk: 'oncelik:desc',
  };
  const [sortKey, setSortKey] = useState('newest');

  // Server-side liste
  const arizaList = useServerList('/pk/arizalar', {
    pageSize: 100,
    initialSort: 'created_at:desc',
    initialFilters: {},
  });

  useEffect(() => {
    arizaList.setSort(SORT_MAP[sortKey] || 'created_at:desc');
  }, [sortKey]);// eslint-disable-line

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [aRes, eRes, bRes, fRes] = await Promise.all([
        axios.get(`${API}/pk/arizalar`, { withCredentials: true }),
        axios.get(`${API}/pk/ekipmanlar`, { withCredentials: true }),
        axios.get(`${API}/pk/binalar`, { withCredentials: true }),
        axios.get(`${API}/pk/firmalar`, { withCredentials: true })
      ]);
      // Özet için tam arıza listesi (geri uyumlu paginate=0)
      setArizalarTumu(Array.isArray(aRes.data) ? aRes.data : (aRes.data.items || []));
      setEkipmanlar(eRes.data);
      setBinalar(bRes.data);
      setFirmalar(fRes.data);
    } catch (err) { console.error(err); }
    finally { setLoadingMeta(false); }
  };

  const refetchAll = () => {
    fetchAll();
    arizaList.refetch();
  };

  const resetForm = () => {
    setFormData({...EMPTY_FORM});
    setEditMode(false);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    if (!formData.ekipman_id || !formData.aciklama) return;
    setSubmitting(true);
    try {
      if (editMode && editingId) {
        await axios.put(`${API}/pk/arizalar/${editingId}`, formData, { withCredentials: true });
        toast.success('Arıza kaydı güncellendi');
      } else {
        await axios.post(`${API}/pk/arizalar`, formData, { withCredentials: true });
        toast.success('Arıza kaydı oluşturuldu');
      }
      setDialogOpen(false);
      resetForm();
      refetchAll();
    } catch (err) { toast.error('Hata: ' + (err.response?.data?.detail || err.message)); }
    finally { setSubmitting(false); }
  };

  const openEdit = (ariza) => {
    setFormData({
      ekipman_id: ariza.ekipman_id || '',
      bina_id: ariza.bina_id || ekipmanlar.find(e => e.id === ariza.ekipman_id)?.bina_id || '',
      aciklama: ariza.aciklama || '',
      oncelik: ariza.oncelik || 'normal',
      atanan_firma_id: ariza.atanan_firma_id || '',
    });
    setEditMode(true);
    setEditingId(ariza.id);
    setDialogOpen(true);
  };

  const handleClose = async () => {
    if (!selectedAriza) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/pk/arizalar/${selectedAriza.id}`, {
        durum: closeData.durum,
        cozum_aciklamasi: closeData.cozum_aciklamasi
      }, { withCredentials: true });
      setCloseDialogOpen(false);
      setSelectedAriza(null);
      refetchAll();
    } catch (err) { toast.error('Hata: ' + err.message); }
    finally { setSubmitting(false); }
  };

  const getEkipmanAdi = (id) => {
    const ek = ekipmanlar.find(e => e.id === id);
    return ek ? `${ek.alt_tip || ek.ekipman_tipi} (${ek.seri_no})` : id;
  };
  const getFirmaAdi = (id) => firmalar.find(f => f.id === id)?.unvan || id || '—';
  // Arıza için bina adı: kayıtta bina_id yoksa ekipman üzerinden bul
  const getArizaBina = (a) => {
    const bid = a.bina_id || ekipmanlar.find(e => e.id === a.ekipman_id)?.bina_id;
    if (!bid) return '—';
    return binalar.find(b => b.id === bid)?.bina_adi || '—';
  };

  const getSLADurum = (ariza) => {
    if (ariza.durum === 'kapandi' || ariza.durum === 'cozuldu') {
      return ariza.sla_asildi ? { text: `SLA İhlal (${ariza.gecikme_saat?.toFixed(1)}h gecikme)`, color: 'text-red-400' } : { text: 'SLA OK', color: 'text-green-400' };
    }
    // Açık arıza — SLA sayacı
    if (ariza.t_start) {
      const elapsed = (Date.now() - new Date(ariza.t_start).getTime()) / 3600000;
      const sla = ariza.sla_suresi_saat || 12;
      const kalan = sla - elapsed;
      if (kalan <= 0) return { text: `SLA AŞILDI (${Math.abs(kalan).toFixed(1)}h)`, color: 'text-red-500 font-bold animate-pulse' };
      if (kalan <= 3) return { text: `${kalan.toFixed(1)}h kaldı`, color: 'text-orange-400' };
      return { text: `${kalan.toFixed(1)}h kaldı`, color: 'text-gray-400' };
    }
    return { text: '—', color: 'text-gray-500' };
  };

  // Tablo tabanlı PDF export (tüm filtrelenmiş kayıtlar)
  const exportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      const rows = await arizaList.fetchAll();
      if (!rows.length) { toast.error('Aktarılacak kayıt bulunamadı'); return; }
      const headers = ['Bina', 'Ekipman', 'Açıklama', 'Öncelik', 'Durum', 'Atanan Firma', 'SLA', 'Gecikme (saat)', 'Ceza (TL)', 'Çözüm', 'Tarih'];
      const data = rows.map(a => [
        getArizaBina(a),
        getEkipmanAdi(a.ekipman_id),
        a.aciklama || '-',
        ONCELIK_CFG[a.oncelik]?.label || a.oncelik || '-',
        DURUM_CFG[a.durum]?.label || a.durum || '-',
        getFirmaAdi(a.atanan_firma_id),
        getSLADurum(a).text,
        a.gecikme_saat ? a.gecikme_saat.toFixed(1) : '-',
        a.ceza_tutari ? a.ceza_tutari.toLocaleString('tr-TR') : '-',
        a.cozum_aciklamasi || '-',
        a.created_at ? new Date(a.created_at).toLocaleDateString('tr-TR') : '-',
      ]);
      const binaAdi = filterBina ? (binalar.find(b => b.id === filterBina)?.bina_adi || '') : 'Tüm Binalar';
      // Öncelik (3), Durum (4), SLA (6), Gecikme (7), Ceza (8) hücreleri ekrandaki rozet renkleriyle
      const cellStyler = (r, c, raw) => {
        if (c === 3) return ONCELIK_PDF[raw] || null;
        if (c === 4) return ARIZA_DURUM_PDF[raw] || null;
        if (c === 6) return slaStyle(raw);
        if (c === 7 || c === 8) return kirmiziMetin(raw);
        return null;
      };
      await exportTableToPDF(
        headers, data,
        'Arıza / Ticketing Kayıtları',
        `Ariza-Kayitlari-${new Date().toISOString().slice(0, 10)}.pdf`,
        { subtitle: `${binaAdi} · ${rows.length} kayıt`, headerColor: '#dc2626', orientation: 'landscape', pageSize: 'a4', cellStyler }
      );
      toast.success('PDF indirildi');
    } catch (e) {
      toast.error('PDF oluşturulamadı: ' + (e?.message || e));
    } finally {
      setExportingPdf(false);
    }
  };

  const sorted = arizaList.items; // Server-side sort
  const acikSayisi = arizalarTumu.filter(a => ['acik', 'atandi', 'mudahale_edildi'].includes(a.durum)).length;
  const slaIhlalSayisi = arizalarTumu.filter(a => a.sla_asildi).length;
  const toplamCeza = arizalarTumu.reduce((s, a) => s + (a.ceza_tutari || 0), 0);

  // Filtre setter helper
  const filterDurum = arizaList.filters.durum || '';
  const setFilterDurum = (v) => arizaList.setFilter('durum', v || undefined);
  const filterBina = arizaList.filters.bina_id || '';
  const setFilterBina = (v) => arizaList.setFilters({ ...arizaList.filters, bina_id: v || undefined, ekipman_id: undefined });
  const filterEkipman = arizaList.filters.ekipman_id || '';
  const setFilterEkipman = (v) => arizaList.setFilter('ekipman_id', v || undefined);

  if (loadingMeta) return <div className="text-gray-400 text-center py-8">Yükleniyor...</div>;

  return (
    <div className="space-y-6" data-testid="pk-arizalar">
      {/* Özet */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-red-900/20 border-red-700/40"><CardContent className="p-3">
          <p className="text-2xl font-bold text-red-400">{acikSayisi}</p><p className="text-xs text-gray-400">Açık Arıza</p>
        </CardContent></Card>
        <Card className="bg-orange-900/20 border-orange-700/40"><CardContent className="p-3">
          <p className="text-2xl font-bold text-orange-400">{slaIhlalSayisi}</p><p className="text-xs text-gray-400">SLA İhlali</p>
        </CardContent></Card>
        <Card className="bg-purple-900/20 border-purple-700/40"><CardContent className="p-3">
          <p className="text-2xl font-bold text-purple-400">{toplamCeza.toLocaleString('tr-TR')} TL</p><p className="text-xs text-gray-400">Toplam Ceza</p>
        </CardContent></Card>
        <Card className="bg-green-900/20 border-green-700/40"><CardContent className="p-3">
          <p className="text-2xl font-bold text-green-400">{arizalarTumu.filter(a => a.durum === 'kapandi').length}</p><p className="text-xs text-gray-400">Kapatılmış</p>
        </CardContent></Card>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={filterDurum || '_all'} onValueChange={v => setFilterDurum(v === '_all' ? '' : v)}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white w-[160px] h-9 text-sm" data-testid="ariza-durum-filtre"><SelectValue placeholder="Durum Filtre" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white">
              <SelectItem value="_all">Tümü ({arizalarTumu.length})</SelectItem>
              <SelectItem value="acik">Açık ({arizalarTumu.filter(a => a.durum === 'acik').length})</SelectItem>
              <SelectItem value="kapandi">Kapandı</SelectItem>
              <SelectItem value="cozuldu">Çözüldü</SelectItem>
            </SelectContent>
          </Select>
          {/* Bina filtresi */}
          <Select value={filterBina || '_all'} onValueChange={v => setFilterBina(v === '_all' ? '' : v)}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white w-[180px] h-9 text-sm" data-testid="ariza-bina-filtre">
              <Building2 className="w-4 h-4 mr-1 text-gray-400" />
              <SelectValue placeholder="Bina Filtre" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-[280px]">
              <SelectItem value="_all">Tüm Binalar</SelectItem>
              {binalar.map(b => <SelectItem key={b.id} value={b.id}>{b.bina_adi}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* Ekipman filtresi */}
          <Select value={filterEkipman || '_all'} onValueChange={v => setFilterEkipman(v === '_all' ? '' : v)}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white w-[200px] h-9 text-sm" data-testid="ariza-ekipman-filtre">
              <Wrench className="w-4 h-4 mr-1 text-gray-400" />
              <SelectValue placeholder="Ekipman Filtre" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-[280px]">
              <SelectItem value="_all">Tüm Ekipmanlar</SelectItem>
              {ekipmanlar
                .filter(e => !filterBina || e.bina_id === filterBina)
                .map(e => <SelectItem key={e.id} value={e.id}>{e.alt_tip || e.ekipman_tipi} — {e.seri_no}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* Server-side search */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={arizaList.search}
              onChange={(e) => arizaList.setSearch(e.target.value)}
              placeholder="Açıklama, ariza tipi ara..."
              className="bg-gray-800 border-gray-700 text-white pl-8 h-9 w-[220px]"
              data-testid="ariza-search-input"
            />
          </div>
          <SortSelect value={sortKey} onChange={setSortKey} customOptions={[
            { value: 'newest', label: 'En Yeni' }, { value: 'oldest', label: 'En Eski' },
            { value: 'oncelik_yuksek', label: 'Öncelik (Yüksek→Düşük)' }, { value: 'oncelik_dusuk', label: 'Öncelik (Düşük→Yüksek)' }
          ]} />
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={exportPdf} disabled={exportingPdf} variant="outline" className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700 h-9" data-testid="ariza-pdf-btn">
            {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} PDF İndir
          </Button>
          {!readOnly && (
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-red-600 hover:bg-red-700" data-testid="create-ariza-btn">
              <Plus className="w-4 h-4 mr-2" /> Arıza Bildir
            </Button>
          )}
        </div>
      </div>

      {/* Arıza Listesi */}
      <div className="space-y-3">
        {sorted.map(ariza => {
          const durumCfg = DURUM_CFG[ariza.durum] || DURUM_CFG.acik;
          const oncelikCfg = ONCELIK_CFG[ariza.oncelik] || ONCELIK_CFG.normal;
          const sla = getSLADurum(ariza);
          const DurumIcon = durumCfg.icon;

          return (
            <Card key={ariza.id} className={`border ${durumCfg.bg}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <DurumIcon className={`w-4 h-4 ${durumCfg.text}`} />
                      <Badge className={`${oncelikCfg.bg} text-white text-[10px]`}>{oncelikCfg.label}</Badge>
                      <span className="text-xs text-gray-500">{getArizaBina(ariza)}</span>
                    </div>
                    <p className="text-sm text-white mb-1">{ariza.aciklama}</p>
                    <p className="text-xs text-gray-400">{getEkipmanAdi(ariza.ekipman_id)} — {getFirmaAdi(ariza.atanan_firma_id)}</p>
                    {ariza.cozum_aciklamasi && <p className="text-xs text-green-400/70 mt-1">Çözüm: {ariza.cozum_aciklamasi}</p>}
                  </div>
                  <div className="text-right flex flex-col items-end gap-1">
                    <span className={`text-xs ${sla.color}`}>{sla.text}</span>
                    {ariza.ceza_tutari > 0 && <span className="text-xs text-red-400 font-semibold">{ariza.ceza_tutari.toLocaleString('tr-TR')} TL ceza</span>}
                    {!readOnly && (
                      <div className="flex items-center gap-1 mt-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(ariza)}
                          className="h-7 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-900/30" data-testid={`edit-ariza-${ariza.id}`}>
                          <Pencil className="w-3 h-3 mr-1" /> Düzelt
                        </Button>
                        {(ariza.durum === 'acik' || ariza.durum === 'atandi' || ariza.durum === 'mudahale_edildi') && (
                          <Button size="sm" onClick={() => { setSelectedAriza(ariza); setCloseData({ cozum_aciklamasi: '', durum: 'cozuldu' }); setCloseDialogOpen(true); }}
                            className="bg-green-700 hover:bg-green-600 h-7 text-xs" data-testid={`close-ariza-${ariza.id}`}>
                            Kapat
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Server-side pagination */}
      <Pagination
        page={arizaList.page}
        totalPages={arizaList.totalPages}
        total={arizaList.total}
        pageSize={arizaList.pageSize}
        loading={arizaList.loading}
        onPageChange={arizaList.setPage}
        onPageSizeChange={arizaList.setPageSize}
        testIdPrefix="ariza-pagination"
      />

      {/* Yeni Arıza / Düzenleme */}
      <Dialog open={dialogOpen} onOpenChange={v => { if (!v) { setDialogOpen(false); resetForm(); return; } setDialogOpen(v); }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader><DialogTitle data-testid="ariza-form-title">{editMode ? 'Arıza Kaydı Düzenle' : 'Arıza Bildir'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Bina *</Label>
              <Select value={formData.bina_id} onValueChange={v => setFormData({...formData, bina_id: v, ekipman_id: ''})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Bina seçin" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">{binalar.map(b => <SelectItem key={b.id} value={b.id}>{b.bina_adi}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ekipman *</Label>
              <Select value={formData.ekipman_id} onValueChange={v => setFormData({...formData, ekipman_id: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Ekipman seçin" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  {ekipmanlar.filter(e => !formData.bina_id || e.bina_id === formData.bina_id).map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.alt_tip || e.ekipman_tipi} — {e.seri_no}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Öncelik</Label>
                <Select value={formData.oncelik} onValueChange={v => setFormData({...formData, oncelik: v})}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="kritik">Kritik</SelectItem><SelectItem value="yuksek">Yüksek</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem><SelectItem value="dusuk">Düşük</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Atanan Firma</Label>
                <Select value={formData.atanan_firma_id} onValueChange={v => setFormData({...formData, atanan_firma_id: v})}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Firma" /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">{firmalar.map(f => <SelectItem key={f.id} value={f.id}>{f.unvan}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Açıklama *</Label>
              <Textarea value={formData.aciklama} onChange={e => setFormData({...formData, aciklama: e.target.value})} className="bg-gray-800 border-gray-700 text-white" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setDialogOpen(false); resetForm(); }} variant="ghost" className="text-gray-400">İptal</Button>
            <Button onClick={handleSubmit} disabled={submitting} className={editMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'} data-testid="submit-ariza-btn">
              {submitting ? 'Kaydediliyor...' : (editMode ? 'Güncelle' : 'Arıza Oluştur')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kapatma */}
      <Dialog open={closeDialogOpen} onOpenChange={v => { if (!v) { setCloseDialogOpen(false); } }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader><DialogTitle>Arıza Kapat</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-300">{selectedAriza?.aciklama}</p>
            <div className="space-y-2">
              <Label>Durum</Label>
              <Select value={closeData.durum} onValueChange={v => setCloseData({...closeData, durum: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="cozuldu">Çözüldü</SelectItem><SelectItem value="kapandi">Kapandı</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Çözüm Açıklaması *</Label>
              <Textarea value={closeData.cozum_aciklamasi} onChange={e => setCloseData({...closeData, cozum_aciklamasi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCloseDialogOpen(false)} variant="ghost" className="text-gray-400">İptal</Button>
            <Button onClick={handleClose} disabled={submitting} className="bg-green-600 hover:bg-green-700">{submitting ? '...' : 'Kapat & Kaydet'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
